import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import RegisterModal from '../../components/Auth/RegisterModal';
import LoginModal from '../../components/Auth/LoginModal';
import AiAskBox from '../../components/AiAskBox/AiAskBox';
import FeaturePromo from '../../components/FeaturePromo/FeaturePromo';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { CARD_BY_SLUG, MEANING_LANGS, findCardByName, loadContent } from '../../data/cardMeanings';
import usePickerLanguage from './usePickerLanguage';
import { ensureTarotScripts, tarotScriptsReady } from './loadTarotScripts';
import '../FreeTarot/FreeTarot.css';

// Shared template for spread-specific landing pages (love, career, Celtic cross).
// Reuses the vanilla-JS tarot reader with a preselected spread, like ThreeCardTarot.
const SpreadReadingPage = ({ config }) => {
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  usePickerLanguage(config.pickerPage);

  useEffect(() => {
    if (!document.getElementById('tarot-reader-styles')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/tarot-reader/cards-only.css';
      link.id = 'tarot-reader-styles';
      document.head.appendChild(link);
    }

    let cancelled = false;
    const alreadyLoaded = tarotScriptsReady();
    const autoSelectSpread = () => {
      const waitForApp = setInterval(() => {
        if (cancelled || (window.tarotApp && typeof window.tarotApp.selectSpread === 'function')) {
          clearInterval(waitForApp);
          if (!cancelled && window.tarotApp) window.tarotApp.selectSpread(config.spreadId);
        }
      }, 100);
      setTimeout(() => clearInterval(waitForApp), 5000);
    };

    ensureTarotScripts()
      .then(() => {
        if (cancelled) return;
        if (alreadyLoaded) window.initTarotApp();
        autoSelectSpread();
      })
      .catch((error) => {
        console.error('Failed to load tarot scripts:', error);
      });

    return () => {
      cancelled = true;
      if (window.tarotApp) {
        window.tarotApp = null;
      }
      if (typeof window.cleanupTarotDeck === 'function') {
        window.cleanupTarotDeck();
      }
    };
  }, [config.spreadId]);

  useEffect(() => {
    if (!config.longCardMeaning) return undefined;
    let content = null;
    let cancelled = false;

    const addParagraphs = (parent, text) => {
      text.split('\n\n').forEach((paragraph) => {
        const p = document.createElement('p');
        p.textContent = paragraph;
        parent.appendChild(p);
      });
    };

    const apply = () => {
      if (meaningLang() !== loadedLang) {
        ensureContent().then(() => {
          if (!cancelled) apply();
        });
        return;
      }
      if (!content) return;
      const root = document.getElementById('interpretationContent');
      if (!root) return;
      root.querySelectorAll('.interpretation-card').forEach((cardEl) => {
        if (cardEl.querySelector('.meaning-long')) return;
        const name = cardEl.querySelector('.card-title')?.textContent?.trim();
        const card = (cardEl.dataset.slug && CARD_BY_SLUG[cardEl.dataset.slug])
          || (name ? findCardByName(name) : null);
        const entry = card && content[card.slug];
        if (!entry) return;
        const side = cardEl.querySelector('.reversed-badge') ? entry.reversed : entry.upright;
        const block = document.createElement('div');
        block.className = 'meaning-long';
        [
          ['Meaning', side.general],
          ['Love', side.love],
          ['Career', side.career],
          ['Money', side.finances],
        ].forEach(([label, text]) => {
          if (!text) return;
          const heading = document.createElement('h4');
          heading.textContent = label;
          block.appendChild(heading);
          addParagraphs(block, text);
        });
        const link = cardEl.querySelector('.card-meaning-link');
        if (link) cardEl.insertBefore(block, link);
        else cardEl.appendChild(block);
      });
    };

    const meaningLang = () => {
      const pickerLang = window.NOW_TAROT_PICKER && window.NOW_TAROT_PICKER.lang;
      return MEANING_LANGS.includes(pickerLang) ? pickerLang : 'en';
    };
    let loadedLang = '';
    const ensureContent = () => {
      const lang = meaningLang();
      if (content && loadedLang === lang) return Promise.resolve();
      return loadContent(lang).then((data) => {
        if (cancelled) return;
        content = data;
        loadedLang = lang;
      });
    };
    ensureContent().then(() => {
      if (!cancelled) apply();
    });

    const root = document.getElementById('interpretationContent');
    const observer = new MutationObserver(apply);
    if (root) observer.observe(root, { childList: true });

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [config.longCardMeaning]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: config.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  return (
    <div className="free-tarot-page">
      <Helmet>
        <title>{config.metaTitle}</title>
        <meta name="description" content={config.metaDescription} />
        <meta name="keywords" content={config.metaKeywords} />
        <link rel="canonical" href={`https://nowtarot.com${config.path}`} />
        {(config.alternates || []).map((alt) => (
          <link key={alt.hrefLang} rel="alternate" hrefLang={alt.hrefLang} href={alt.href} />
        ))}
        <meta property="og:title" content={config.metaTitle} />
        <meta property="og:description" content={config.metaDescription} />
        <meta property="og:image" content="https://nowtarot.com/og-free-tarot.png" />
        <meta property="og:url" content={`https://nowtarot.com${config.path}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={config.metaTitle} />
        <meta name="twitter:description" content={config.metaDescription} />
        <meta name="twitter:image" content="https://nowtarot.com/og-free-tarot.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Header />
      <div className="stars"></div>
      <div className="container">
        <header className="tarot-header">
          <h1 className="page-title">{config.heading}</h1>
        </header>

        <section className="intro-section">
          <p>{config.intro}</p>
          <h2>How It Works</h2>
          <p>{config.howItWorks}</p>
        </section>

        <main className="main-content">
          <section className="deck-section visible" id="deckSection">

            <div className="spread-select-wrap" style={{ display: 'none' }}>
              <label htmlFor="spreadSelect">Choose your spread:</label>
              <select id="spreadSelect" className="spread-select"></select>
            </div>

            <p className="cards-needed" id="cardsNeeded">
              Please choose {config.cardCount} Tarot card{config.cardCount > 1 ? 's' : ''} for your reading!
            </p>

            <div className="options-inline" style={{ display: 'none' }}>
              <label className="toggle-label">
                <input type="checkbox" id="enableReversed" className="toggle-checkbox" />
                <span className="toggle-switch"></span>
                <span>Enable Reversed Cards</span>
              </label>
              <div className="slider-container" id="sliderContainer">
                <label htmlFor="reversedChance">Chance: <span id="chanceValue">25</span>%</label>
                <input type="range" id="reversedChance" min="5" max="50" defaultValue="25" className="slider" />
              </div>
            </div>

            <div id="cardarea-wrap" className="cardarea-wrap">
              <div id="cardarea"></div>
            </div>

            <p className="deck-instruction" id="deckInstruction">
              <em>Focus on your question, then click {config.cardCount > 1 ? `${config.cardCount} cards` : 'a card'} for your reading!</em>
            </p>

            <div className="deck-controls deck-controls-single">
              <button type="button" className="btn btn-reset" id="shuffle-again">
                Shuffle Again
              </button>
            </div>

            <button className="btn btn-interpret" id="interpretBtn" disabled style={{ display: 'none' }}>
              ✨ Interpret Your Cards ✨
            </button>
          </section>

          <section className="interpretation-section" id="interpretationSection">
            <h3>Your Reading Interpretation</h3>
            <div className="interpretation-content" id="interpretationContent"></div>
            <button className="btn btn-new-reading" id="newReadingBtn">
              Start a New Reading
            </button>
          </section>

          <section className="cta-section ft-ask-band">
            <h3>Ask the AI tarot</h3>
            <p>Type a question. Get cards and an answer — no signup.</p>
            <AiAskBox onSignUp={() => setShowRegister(true)} />
          </section>

          <FeaturePromo variant="page" seed={config.path} exclude={config.path} />

          {config.contentSections.map((section) => (
            <section className="about-section" key={section.heading}>
              <h3>{section.heading}</h3>
              <div className="readings-info">
                {section.items.map((item) => (
                  <div className="reading-type" key={item.title}>
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section className="faq-section">
            <h3>{config.faqHeading}</h3>
            <div className="faq-list">
              {config.faq.map((item) => (
                <div className="faq-item" key={item.question}>
                  <h4>{item.question}</h4>
                  <p>{item.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="about-section" style={{ textAlign: 'center' }}>
            <p>{config.crossLinks}</p>
          </section>
        </main>

      </div>
      <Footer />
      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
        />
      )}
    </div>
  );
};

export default SpreadReadingPage;
